/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { Box, FormControl, InputLabel, Select, SelectChangeEvent, TextField, MenuItem, Grid, FormHelperText, List, ListItem, ListItemIcon, ListItemText, Checkbox, FormControlLabel, Switch, Alert, ToggleButtonGroup, ToggleButton, Collapse, useTheme } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Franchise, Persona, Store, User, Tenant } from '../../../../app/services/api/generated';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import { isEmailValid } from '../../../../utils';
import useLogError from '../../../../hooks/useLogError';
import { UpsertUserRequest } from '../types/UpsertUserRequest';
import LeavePageModal from '../../../../components/shared/dialogs/LeavePageModal';
import { UserContext } from '../../../../components/shared/useUser';

type UserAccessLevel = 'store' | 'franchise' | 'tenant';

interface SubmitModalProps {
    openModal: boolean;
    onConfirm: (request: UpsertUserRequest) => Promise<void> | void;
    editUser: User | undefined;
    onCancel: () => void;
    onResetPassword: () => void;
}

const CreateUpdateUserModal: React.FC<SubmitModalProps> = ({ openModal, onConfirm, editUser, onCancel, onResetPassword }) => {
  const { t } = useTranslation('manageUsers');
  const { getPersonas } = useNswagClient();
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const [accessibleStores, setAccessibleStores] = useState<Store[]>([]);
  const [accessibleFranchises, setAccessibleFranchises] = useState<Franchise[]>([]);
  const [accessibleTenants, setAccessibleTenants] = useState<Tenant[]>([]);
  const [accessLevel, setAccessLevel] = useState<UserAccessLevel>('store');
  const [expandedDistricts, setExpandedDistricts] = useState<Set<string>>(new Set());

  const hasTenantLevelAccess = user?.hasTenantLevelAccess ?? (user?.tenants?.length ?? 0) > 0;
  const hasFranchiseLevelAccess = user?.hasFranchiseLevelAccess ?? user?.allStores ?? false;

  const [originalUserData, setOriginalUserData] = useState<{
        stores: string[];
        franchiseIds: number[];
        tenantIds: number[];
    }>({ stores: [], franchiseIds: [], tenantIds: [] });

  useEffect(() => {
    if (user) {
      setAccessibleStores(user?.stores || []);
      setAccessibleFranchises(user?.franchises || []);
      setFranchiseList(user?.franchises || []);
      setAccessibleTenants(user?.tenants || []);
    }
  }, [user]);

  const [request, setRequest] = useState<UpsertUserRequest>({
    emailAddress: '',
    franchiseName: '',
    personaId: undefined,
    useNewUI: true,
    stores: [],
    franchiseIds: [],
    tenantIds: [],
  });

  const [initialRequest, setInitialRequest] = useState<UpsertUserRequest | undefined>(undefined);
  const [errors, setErrors] = useState<Partial<Record<keyof UpsertUserRequest, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof UpsertUserRequest, boolean>>>({});
  const [franchiseList, setFranchiseList] = useState<Franchise[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const { logError } = useLogError();

  useEffect(() => {
    if (openModal) {
      getData();
    }
  }, [editUser, openModal]);

  const determineAccessLevel = (editUser: User | undefined): UserAccessLevel => {

    if (editUser?.tenants && editUser.tenants.length > 0) {
      return 'tenant';
    }

    if (editUser?.franchises && editUser.franchises.length > 0) {
      return 'franchise';
    }

    return 'store';
  };

  const getData = () => {

    setIsLoading(true);

    const level = determineAccessLevel(editUser);

    setAccessLevel(level);

    const stores = level === 'store'
      ? editUser?.stores?.filter(s => s.storeNumber !== undefined).map(s => s.storeNumber ?? '') ?? []
      : [];

    const franchiseIds = level === 'franchise'
      ? editUser?.franchises?.filter(f => f.franchiseId !== undefined).map(f => f.franchiseId ?? -1) ?? []
      : [];

    const tenantIds = editUser?.tenants?.filter(t => t.id !== undefined).map(t => t.id ?? -1) ?? [];

    const newRequest: UpsertUserRequest = {
      emailAddress: editUser?.emailAddress ?? '',
      personaId: editUser?.persona?.id,
      franchiseName: editUser?.franchiseName ?? '',
      useNewUI: true,
      stores,
      franchiseIds,
      tenantIds,
    };

    setOriginalUserData({ stores, franchiseIds, tenantIds });
    setRequest(newRequest);
    setInitialRequest(newRequest);
    setErrors({});
    setTouched({});
    getPersonaData();
    setIsLoading(false);

  };

  const getPersonaData = async () => {
    try {
      const result = await getPersonas();
      if (result?.data) {
        setPersonas(result.data);
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const groupedStores = useMemo(() => {
    const franchise = franchiseList.find(f => f.franchiseName === request.franchiseName);
    if (!franchise) return { groups: [], unassigned: [] };

    const filteredStores = accessibleStores.filter(store =>
      store.franchiseId === franchise.franchiseId ||
      store.franchiseName?.toLocaleLowerCase() === request.franchiseName.toLocaleLowerCase(),
    );

    const districtStoreMap = new Map<string, Store[]>();
    const unassigned: Store[] = [];

    for (const store of filteredStores) {
      const districtName = store.districtName;
      if (districtName) {
        if (!districtStoreMap.has(districtName)) {
          districtStoreMap.set(districtName, []);
        }
        districtStoreMap.get(districtName)!.push(store);
      } else {
        unassigned.push(store);
      }
    }

    // Expand all districts by default on first load
    if (expandedDistricts.size === 0 && districtStoreMap.size > 0) {
      setExpandedDistricts(new Set(districtStoreMap.keys()));
    }

    const groups = Array.from(districtStoreMap.entries()).map(([name, stores]) => ({
      districtName: name,
      stores,
    }));

    return { groups, unassigned };
  }, [accessibleStores, franchiseList, request.franchiseName]);

  const toggleDistrict = (districtName: string) => {
    setExpandedDistricts(prev => {
      const next = new Set(prev);
      if (next.has(districtName)) {
        next.delete(districtName);
      } else {
        next.add(districtName);
      }
      return next;
    });
  };

  const handleDistrictToggleAll = (stores: Store[]) => {
    const storeNumbers = stores.map(s => s.storeNumber ?? '').filter(Boolean);
    const selected = request.stores || [];
    const allSelected = storeNumbers.every(sn => selected.includes(sn));

    if (allSelected) {
      // Deselect all in this district
      setRequest(prev => ({
        ...prev,
        stores: (prev.stores || []).filter(s => !storeNumbers.includes(s)),
      }));
    } else {
      // Select all in this district
      const newStores = new Set([...selected, ...storeNumbers]);
      setRequest(prev => ({
        ...prev,
        stores: Array.from(newStores),
      }));
    }
  };

  const isDistrictAllSelected = (stores: Store[]): boolean => {
    const storeNumbers = stores.map(s => s.storeNumber ?? '').filter(Boolean);
    const selected = request.stores || [];
    return storeNumbers.length > 0 && storeNumbers.every(sn => selected.includes(sn));
  };

  const isDistrictPartiallySelected = (stores: Store[]): boolean => {
    const storeNumbers = stores.map(s => s.storeNumber ?? '').filter(Boolean);
    const selected = request.stores || [];
    const selectedCount = storeNumbers.filter(sn => selected.includes(sn)).length;
    return selectedCount > 0 && selectedCount < storeNumbers.length;
  };

  const isFormDirty = () => {
    if (!request || !initialRequest) return false;
    return JSON.stringify(request) !== JSON.stringify(initialRequest);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validateField = (field: keyof UpsertUserRequest, value: any): string | undefined => {
    if (field === 'emailAddress' && value) {
      return isEmailValid(value) ? undefined : t('invalidEmail');
    }
    if (['name', 'emailAddress', 'franchiseName', 'personaId'].includes(field) && !value) {
      return t(`required${field}`);
    }
    return undefined;
  };

  const handleChange = (field: keyof UpsertUserRequest) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }> | SelectChangeEvent<string | number>,
  ) => {
    const value = event.target.value;
    if (field === 'franchiseName') {
      setRequest((prev) => ({ ...prev, franchiseName: value as string, personaId: undefined }));
    } else {
      setRequest((prev) => ({ ...prev, [field]: value }));
    }
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleBlur = (field: keyof UpsertUserRequest) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, request[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleCancel = () => {
    if (isFormDirty()) {
      setShowLeaveModal(true);
    } else {
      onCancel();
      setRequest({
        emailAddress: '',
        franchiseName: '',
        personaId: undefined,
        useNewUI: true,
        stores: [],
        franchiseIds: [],
        tenantIds: [],
      });
      setAccessLevel('store');
    }
  };

  const handleConfirm = async () => {
    const emailValid = isEmailValid(request.emailAddress);
    if (!emailValid) {
      setErrors((prev) => ({ ...prev, emailAddress: t('invalidEmail') }));
      setTouched((prev) => ({ ...prev, emailAddress: true }));
      return;
    }

    const newErrors: Partial<Record<keyof UpsertUserRequest, string>> = {};

    (['emailAddress', 'franchiseName', 'personaId'] as (keyof UpsertUserRequest)[]).forEach((field) => {
      const error = validateField(field, request[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({
        emailAddress: true,
        franchiseName: true,
        personaId: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(request);
      setRequest({
        emailAddress: '',
        franchiseName: '',
        personaId: undefined,
        useNewUI: true,
        stores: [],
        franchiseIds: [],
        tenantIds: [],
      });
      setAccessLevel('store');
      onCancel();
    } catch (error) {
      logError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = (() => {
    if (!request.personaId || !isEmailValid(request.emailAddress)) {
      return false;
    }
    if (accessLevel === 'tenant') {
      return (request.tenantIds?.length ?? 0) > 0;
    }
    if (accessLevel === 'franchise') {
      return (request.franchiseIds?.length ?? 0) > 0;
    }
    return !!request.franchiseName && (request.stores?.length ?? 0) > 0;
  })();

  if (isLoading) {
    return <LoadingWheel />;
  }

  return (
    <>
      <Dialog
        open={openModal}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        aria-labelledby="customized-dialog-title"
        aria-describedby="customized-dialog-description"
        sx={{
          '& .MuiDialog-container': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="displayMD">{t('manageUser')}</Typography>
            <Typography variant="textMD"
              color="text.secondary">
              {t('editExistingUser')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <IconButton
              aria-label="close"
              onClick={handleCancel}
              sx={{ color: (theme) => theme.palette.custom.gray[700] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 7, pb: 4 }}>
          <Grid container
            spacing={4}
            sx={{ maxWidth: '100%' }}
            pt={5}>
            <Grid item
              xs={12}
              md={5}>
              <FormControl fullWidth
                error={touched.emailAddress && !!errors.emailAddress}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label={t('email')}
                  name="email"
                  value={request.emailAddress}
                  onChange={handleChange('emailAddress')}
                  onBlur={handleBlur('emailAddress')}
                  error={touched.emailAddress && !!errors.emailAddress}
                />
                <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                  {touched.emailAddress && errors.emailAddress ? errors.emailAddress : ' '}
                </FormHelperText>
              </FormControl>
              <FormControl
                required
                variant="outlined"
                size="small"
                fullWidth
                error={touched.franchiseName && !!errors.franchiseName}
              >
                <InputLabel id="franchise-select-label">{t('company')}</InputLabel>
                <Select
                  labelId="franchise-select-label"
                  label={t('company')}
                  name="franchiseName"
                  value={request.franchiseName}
                  onChange={handleChange('franchiseName')}
                  onBlur={handleBlur('franchiseName')}
                >
                  {franchiseList.map((franchise) => (
                    <MenuItem key={franchise.franchiseName}
                      value={franchise.franchiseName}>
                      {franchise.franchiseName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                  {touched.franchiseName && errors.franchiseName ? errors.franchiseName : ' '}
                </FormHelperText>
              </FormControl>
              <FormControl
                required
                variant="outlined"
                size="small"
                fullWidth
                error={touched.personaId && !!errors.personaId}
              >
                <InputLabel id="user-role-select-label">{t('role')}</InputLabel>
                <Select
                  labelId="user-role-select-label"
                  label={t('role')}
                  name="personaId"
                  value={request.personaId ?? ''}
                  onChange={handleChange('personaId')}
                  onBlur={handleBlur('personaId')}
                  disabled={!request.franchiseName}
                >
                  {personas
                    .filter((p) => {
                      const selectedFranchise = franchiseList.find(f => f.franchiseName === request.franchiseName);
                      const selectedFranchiseId = selectedFranchise?.franchiseId;
                      const selectedTenantId = selectedFranchise?.tenantId;

                      if (p.tenantIds && p.tenantIds.length > 0 && selectedTenantId) {
                        if (p.tenantIds.includes(selectedTenantId)) {
                          return true;
                        }
                      }

                      if (p.franchiseIds && p.franchiseIds.length > 0 && selectedFranchiseId) {
                        if (p.franchiseIds.includes(selectedFranchiseId)) {
                          return true;
                        }
                      }

                      if (p.franchiseName === request.franchiseName) {
                        return true;
                      }

                      if (!p.franchiseName && p.tenantId && selectedTenantId === p.tenantId) {
                        return true;
                      }

                      return false;
                    })
                    .map((p) => (
                      <MenuItem key={p.id}
                        value={p.id}>
                        {p.description}
                      </MenuItem>
                    ))}
                </Select>
                <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                  {touched.personaId && errors.personaId ? errors.personaId : ' '}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mb: 2 }}>
            <Typography variant="textSM"
              sx={{ mb: 1, display: 'block' }}>
              {t('accessLevel')}
            </Typography>
            <ToggleButtonGroup
              value={accessLevel}
              exclusive
              onChange={(_, value) => {
                if (value) {
                  setAccessLevel(value);
                  if (value === 'tenant') {
                    setRequest(prev => ({
                      ...prev,
                      stores: [],
                      franchiseIds: [],
                      tenantIds: originalUserData.tenantIds,
                    }));
                  } else if (value === 'franchise') {
                    setRequest(prev => ({
                      ...prev,
                      stores: [],
                      franchiseIds: originalUserData.franchiseIds,
                      tenantIds: [],
                    }));
                  } else {
                    setRequest(prev => ({
                      ...prev,
                      stores: originalUserData.stores,
                      franchiseIds: [],
                      tenantIds: [],
                    }));
                  }
                }
              }}
              size="small"
            >
              <ToggleButton value="store">{t('storeLevel')}</ToggleButton>
              {hasFranchiseLevelAccess && (
                <ToggleButton value="franchise">{t('franchiseLevel')}</ToggleButton>
              )}
              {(hasTenantLevelAccess || accessibleTenants.length > 0) && (
                <ToggleButton value="tenant">{t('tenantLevel')}</ToggleButton>
              )}
            </ToggleButtonGroup>
          </Box>

          {accessLevel === 'tenant' && (
            <>
              <Alert severity="info"
                sx={{ mb: 2 }}>
                {t('tenantLevelInfo')}
              </Alert>
              <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ccc', borderRadius: 1 }}>
                {accessibleTenants.filter(t => t.id !== undefined).map(tenant => (
                  <ListItem
                    key={tenant.id}
                    onClick={() => {
                      let selected = request.tenantIds || [];

                      if (selected.includes(tenant.id ?? -1)) {
                        selected = selected.filter(id => id !== tenant.id);
                      } else {
                        selected = [...selected, tenant.id ?? -1];
                      }

                      setRequest(prev => ({
                        ...prev,
                        tenantIds: selected,
                      }));
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={request.tenantIds?.includes(tenant.id ?? -1) || false}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={tenant.name} />
                  </ListItem>
                ))}
              </List>
              <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                {' '}
              </FormHelperText>
            </>
          )}

          {accessLevel === 'franchise' && (
            <>
              <Alert severity="info"
                sx={{ mb: 2 }}>
                {t('franchiseLevelInfo')}
              </Alert>
              <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ccc', borderRadius: 1 }}>
                {accessibleFranchises.filter(f => f.franchiseId !== undefined).map(franchise => (
                  <ListItem
                    key={franchise.franchiseId}
                    onClick={() => {
                      let selected = request.franchiseIds || [];

                      if (selected.includes(franchise.franchiseId ?? -1)) {
                        selected = selected.filter(id => id !== franchise.franchiseId);
                      } else {
                        selected = [...selected, franchise.franchiseId ?? -1];
                      }

                      setRequest(prev => ({
                        ...prev,
                        franchiseIds: selected,
                      }));
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={request.franchiseIds?.includes(franchise.franchiseId ?? -1) || false}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={franchise.franchiseName} />
                  </ListItem>
                ))}
              </List>
              <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                {touched.franchiseIds && errors.franchiseIds ? errors.franchiseIds : ' '}
              </FormHelperText>
            </>
          )}

          {accessLevel === 'store' && (
            <>
              <Alert severity="info"
                sx={{ mb: 2 }}>
                {t('storeLevelInfo')}
              </Alert>
              <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #ccc', borderRadius: 1 }}>
                {groupedStores.groups.map(({ districtName, stores }) => (
                  <Box key={districtName}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        cursor: 'pointer',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: theme.palette.custom?.gray?.[50] ?? '#fafafa',
                        '&:hover': { bgcolor: theme.palette.custom?.gray?.[100] ?? '#f5f5f5' },
                      }}
                      onClick={() => toggleDistrict(districtName)}
                    >
                      <Checkbox
                        size="small"
                        checked={isDistrictAllSelected(stores)}
                        indeterminate={isDistrictPartiallySelected(stores)}
                        tabIndex={-1}
                        disableRipple
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDistrictToggleAll(stores);
                        }}
                        sx={{ p: 0.25 }}
                      />
                      <Typography
                        variant="textSM"
                        sx={{ fontWeight: 600, flex: 1, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem', color: theme.palette.custom?.gray?.[600] ?? '#666' }}
                      >
                        {districtName}
                      </Typography>
                      <Typography
                        variant="textSM"
                        sx={{ fontSize: '0.7rem', color: theme.palette.custom?.gray?.[500] ?? '#888', mr: 0.5 }}
                      >
                        {stores.filter(s => request.stores?.includes(s.storeNumber ?? '') || false).length}/{stores.length}
                      </Typography>
                      {expandedDistricts.has(districtName) ? <ExpandLess sx={{ fontSize: 18, color: theme.palette.custom?.gray?.[400] ?? '#999' }} /> : <ExpandMore sx={{ fontSize: 18, color: theme.palette.custom?.gray?.[400] ?? '#999' }} />}
                    </Box>
                    <Collapse in={expandedDistricts.has(districtName)}
                      timeout="auto"
                      unmountOnExit>
                      <List component="div"
                        disablePadding
                        sx={{ borderLeft: '2px solid', borderColor: 'divider', ml: 2.5 }}>
                        {stores.map(store => (
                          <ListItem
                            key={store.storeNumber}
                            dense
                            sx={{ pl: 1.5, py: 0, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                            onClick={() => {
                              let selected = request.stores || [];
                              if (store.storeNumber === undefined) return;
                              if (selected.includes(store.storeNumber)) {
                                selected = selected.filter(s => s !== store.storeNumber);
                              } else {
                                selected = [...selected, store.storeNumber];
                              }
                              setRequest(prev => ({ ...prev, stores: selected }));
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <Checkbox
                                size="small"
                                edge="start"
                                checked={request.stores?.includes(store.storeNumber ?? '-1') || false}
                                tabIndex={-1}
                                disableRipple
                                sx={{ p: 0.25 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={store.storeName}
                              primaryTypographyProps={{ variant: 'textSM' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                ))}

                {groupedStores.unassigned.length > 0 && groupedStores.groups.length > 0 && (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        cursor: 'pointer',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: theme.palette.custom?.gray?.[50] ?? '#fafafa',
                        '&:hover': { bgcolor: theme.palette.custom?.gray?.[100] ?? '#f5f5f5' },
                      }}
                      onClick={() => toggleDistrict('__unassigned__')}
                    >
                      <Checkbox
                        size="small"
                        checked={isDistrictAllSelected(groupedStores.unassigned)}
                        indeterminate={isDistrictPartiallySelected(groupedStores.unassigned)}
                        tabIndex={-1}
                        disableRipple
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDistrictToggleAll(groupedStores.unassigned);
                        }}
                        sx={{ p: 0.25 }}
                      />
                      <Typography
                        variant="textSM"
                        sx={{ fontWeight: 600, flex: 1, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem', color: theme.palette.custom?.gray?.[600] ?? '#666' }}
                      >
                        {t('unassignedDistrict')}
                      </Typography>
                      <Typography
                        variant="textSM"
                        sx={{ fontSize: '0.7rem', color: theme.palette.custom?.gray?.[500] ?? '#888', mr: 0.5 }}
                      >
                        {groupedStores.unassigned.filter(s => request.stores?.includes(s.storeNumber ?? '') || false).length}/{groupedStores.unassigned.length}
                      </Typography>
                      {expandedDistricts.has('__unassigned__') ? <ExpandLess sx={{ fontSize: 18, color: theme.palette.custom?.gray?.[400] ?? '#999' }} /> : <ExpandMore sx={{ fontSize: 18, color: theme.palette.custom?.gray?.[400] ?? '#999' }} />}
                    </Box>
                    <Collapse in={expandedDistricts.has('__unassigned__')}
                      timeout="auto"
                      unmountOnExit>
                      <List component="div"
                        disablePadding
                        sx={{ borderLeft: '2px solid', borderColor: 'divider', ml: 2.5 }}>
                        {groupedStores.unassigned.map(store => (
                          <ListItem
                            key={store.storeNumber}
                            dense
                            sx={{ pl: 1.5, py: 0, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                            onClick={() => {
                              let selected = request.stores || [];
                              if (store.storeNumber === undefined) return;
                              if (selected.includes(store.storeNumber)) {
                                selected = selected.filter(s => s !== store.storeNumber);
                              } else {
                                selected = [...selected, store.storeNumber];
                              }
                              setRequest(prev => ({ ...prev, stores: selected }));
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <Checkbox
                                size="small"
                                edge="start"
                                checked={request.stores?.includes(store.storeNumber ?? '-1') || false}
                                tabIndex={-1}
                                disableRipple
                                sx={{ p: 0.25 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={store.storeName}
                              primaryTypographyProps={{ variant: 'textSM' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                )}

                {/* Fallback: if no districts exist, show flat list */}
                {groupedStores.groups.length === 0 && groupedStores.unassigned.map(store => (
                  <ListItem
                    key={store.storeNumber}
                    onClick={() => {
                      let selected = request.stores || [];
                      if (store.storeNumber === undefined) return;
                      if (selected.includes(store.storeNumber)) {
                        selected = selected.filter(s => s !== store.storeNumber);
                      } else {
                        selected = [...selected, store.storeNumber];
                      }
                      setRequest(prev => ({ ...prev, stores: selected }));
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={request.stores?.includes(store.storeNumber ?? '-1') || false}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={store.storeName} />
                  </ListItem>
                ))}
              </List>
              <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                {touched.stores && errors.stores ? errors.stores : (!request.franchiseName ? t('selectCompany') : ' ')}
              </FormHelperText>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ pr: 7, pb: 7, pt: 2, justifyContent: 'flex-end' }}>
          <Button variant="secondary"
            size="lg"
            onClick={handleCancel}
            disabled={isSubmitting}
            sx={{ borderRadius: '8px', px: 3 }}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            disabled={!isValid || isSubmitting}
            onClick={handleConfirm}
            sx={{ borderRadius: '8px', px: 3 }}
          >
            {isSubmitting ? t('saving') || 'Saving...' : t('save')}
          </Button>
        </DialogActions>
      </Dialog >
      <LeavePageModal
        open={showLeaveModal}
        onConfirmUrl=""
        onClose={() => setShowLeaveModal(false)}
        onConfirm={() => {
          handleConfirm();
          setShowLeaveModal(false);
        }}
        onCancel={() => {
          setShowLeaveModal(false);
          onCancel();
          setRequest({
            emailAddress: '',
            franchiseName: '',
            personaId: undefined,
            useNewUI: true,
            stores: [],
            franchiseIds: [],
            tenantIds: [],
          });
          setAccessLevel('store');
        }}
      />
    </>
  );
};

export default CreateUpdateUserModal;
